'use client';

/**
 * @fileoverview PageViewTracker — fires a named category event on mount.
 *
 * Usage in Server Component pages:
 *   <PageViewTracker eventName="pricing_page_view" />
 */
import { useEffect, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import type { AnalyticsEventName, BaseEventParams } from './types';
import { trackEvent } from './events';

interface PageViewTrackerProps {
  eventName: AnalyticsEventName;
  /** Extra params merged into the event payload. */
  extra?: BaseEventParams;
}

function PageViewTrackerInner({ eventName, extra }: PageViewTrackerProps) {
  const searchParams = useSearchParams();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const utmSource = searchParams?.get('utm_source') ?? undefined;
    const utmMedium = searchParams?.get('utm_medium') ?? undefined;
    const utmCampaign = searchParams?.get('utm_campaign') ?? undefined;
    const utmContent = searchParams?.get('utm_content') ?? undefined;
    const utmTerm = searchParams?.get('utm_term') ?? undefined;

    const params: BaseEventParams = {
      landing_url: typeof window !== 'undefined' ? window.location.href : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
      ...(utmSource && { utm_source: utmSource }),
      ...(utmMedium && { utm_medium: utmMedium }),
      ...(utmCampaign && { utm_campaign: utmCampaign }),
      ...(utmContent && { utm_content: utmContent }),
      ...(utmTerm && { utm_term: utmTerm }),
      timestamp: Date.now(),
      ...extra,
    };

    trackEvent(eventName, params);
  }, [eventName, extra, searchParams]);

  return null;
}

export function PageViewTracker({ eventName, extra }: PageViewTrackerProps) {
  return (
    <Suspense fallback={null}>
      <PageViewTrackerInner eventName={eventName} extra={extra} />
    </Suspense>
  );
}
